// src/components/HomeContent.js
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ContentWrapper = styled(motion.div)`
  position: ${({ $isPageOpen, $isReturning }) =>
    $isPageOpen ? ($isReturning ? 'absolute' : 'fixed') : 'absolute'};
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  overflow-y: ${({ $isPageOpen }) => ($isPageOpen ? 'hidden' : 'auto')};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  class-name: home-content-area;
`;

const Mask = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  pointer-events: none;
  z-index: 2;
`;

const NewContentArea = styled(motion.div)`
  flex-grow: 1;
  background: white;
  margin: 0 120px 0 60px;
  overflow-y: auto;
  position: relative;
  opacity: 1;
  z-index: 8;

  @media (max-width: 768px) {
    margin: 0;
  }
`;

const homeContentVariants = {
  collapse: {
    opacity: 1,
    scale: 0.95,
    transition: { duration: 1.4, ease: 'easeInOut' },
  },
  slideIn: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: 'easeInOut' },
  },
  opened: { opacity: 1, scale: 1 },
};

const contentsVariants = {
  hide: { opacity: 0, x: '100%', transition: { duration: 0 } },
  slideIn: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: 'easeInOut' },
  },
  slideOut: {
    opacity: 0,
    x: '100%',
    transition: { duration: 0.5, ease: 'easeInOut' },
  },
  opened: { opacity: 1, x: 0, scale: 1 },
};

const maskVariants = {
  hide: { opacity: 0 },
  collapse: { opacity: 1, transition: { duration: 1.4, ease: 'easeInOut' } },
  slideIn: { opacity: 1, transition: { duration: 0.7, ease: 'easeInOut' } },
  opened: { opacity: 0 },
};

const HomeContent = ({
  homeContent,
  children,
  globalStage,
  activatedSidebar,
  isTransitioning,
  isPageOpen,
  wasSelectedSidebar,
  isReturning,
}) => {
  const isNewContent = !isPageOpen ? activatedSidebar : wasSelectedSidebar;
  const newContentRef = useRef(null);

  useEffect(() => {
    if (globalStage === 'opened' && isNewContent && newContentRef.current) {
      newContentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [globalStage, isNewContent]);

  const getStage = (content) => {
    if (isReturning) {
      if (content === 'new') {
        return 'slideOut';
      } else if (content === 'mask') {
        return 'hide'; // Ensure mask is hidden on returning
      }
      return 'opened'; // Default state for old content on return
    }

    if (isTransitioning) {
      switch (content) {
        case 'old':
          return globalStage === 'opening' ? 'slideIn' : 'collapse';
        case 'new':
          if (globalStage === 'collapse') return 'slideOut';
          if (globalStage === 'opening') return 'slideIn';
          return 'hide';
        case 'mask':
          return globalStage === 'opening' ? 'slideIn' : 'collapse';
        default:
          return 'hide';
      }
    }

    return content === 'new' ? (isNewContent ? 'opened' : 'hide') : 'opened';
  };

  return (
    <>
      <ContentWrapper
        variants={homeContentVariants}
        animate={getStage('old')}
        initial="opened"
        $isPageOpen={isPageOpen}
        $isReturning={isReturning}
      >
        <Mask
          variants={maskVariants}
          animate={getStage('mask')}
          initial="hide"
        />
        {homeContent}
      </ContentWrapper>
      <NewContentArea
        ref={newContentRef}
        variants={contentsVariants}
        animate={getStage('new')}
        initial="hide"
      >
        {isNewContent && children}
      </NewContentArea>
    </>
  );
};

export default HomeContent;
